// src/components/ProjectManagement/TaskAssignmentModal.jsx
import React, { useState, useEffect } from 'react';
import { db } from '../../firebase/config';
import { updateDoc, doc, addDoc, collection, getDocs, query, where, getDoc } from 'firebase/firestore';

// Searchable Worker Dropdown Component
const SearchableWorkerDropdown = ({ workers, selectedWorkerId, onSelect, placeholder }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [filteredWorkers, setFilteredWorkers] = useState(workers);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredWorkers(workers);
    } else {
      const term = searchTerm.toLowerCase();
      const filtered = workers.filter(worker => 
        worker.name?.toLowerCase().includes(term) ||
        worker.role?.toLowerCase().includes(term) ||
        worker.email?.toLowerCase().includes(term)
      );
      setFilteredWorkers(filtered);
    }
  }, [searchTerm, workers]);

  const handleSelect = (workerId) => {
    onSelect(workerId);
    setSearchTerm('');
    setIsOpen(false);
  };

  const selectedWorker = workers.find(w => w.id === selectedWorkerId);

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <div 
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          border: '1px solid #ddd',
          borderRadius: '8px',
          padding: '10px 12px',
          background: 'white',
          cursor: 'pointer',
          minHeight: '42px'
        }}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span style={{ color: selectedWorkerId ? '#333' : '#999' }}>
          {selectedWorkerId ? selectedWorker?.name || 'Select worker...' : placeholder || 'Search or select worker...'}
        </span>
        <span style={{ color: '#999' }}>{isOpen ? '▲' : '▼'}</span>
      </div>
      
      {isOpen && (
        <>
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 999
            }}
            onClick={() => setIsOpen(false)}
          />
          <div style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            background: 'white',
            border: '1px solid #ddd',
            borderRadius: '8px',
            marginTop: '4px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 1000,
            maxHeight: '300px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{ padding: '8px 12px', borderBottom: '1px solid #eee' }}>
              <input
                type="text"
                placeholder="Search by name or role..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '13px',
                  outline: 'none'
                }}
                autoFocus
                onClick={(e) => e.stopPropagation()}
              />
            </div>
            <div style={{ maxHeight: '240px', overflowY: 'auto' }}>
              {filteredWorkers.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                  No workers found
                </div>
              ) : (
                filteredWorkers.map(worker => (
                  <div
                    key={worker.id}
                    onClick={() => handleSelect(worker.id)}
                    style={{
                      padding: '10px 12px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #f5f5f5',
                      transition: 'background 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                  >
                    <div style={{ fontWeight: '500', fontSize: '14px' }}>{worker.name}</div>
                    <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                      {worker.role?.replace('-', ' ')} 
                      {worker.email && <span style={{ marginLeft: '8px' }}>✉️ {worker.email}</span>}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Searchable Multi-Worker Selector Component
const SearchableMultiWorkerSelector = ({ workers, selectedWorkerIds, onToggle }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const filteredWorkers = searchTerm.trim() === '' 
    ? workers 
    : workers.filter(worker => 
        worker.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        worker.role?.toLowerCase().includes(searchTerm.toLowerCase())
      );

  const selectedCount = selectedWorkerIds.length;

  const getSelectedNames = () => {
    return selectedWorkerIds.map(id => {
      const worker = workers.find(w => w.id === id);
      return worker?.name || 'Unknown';
    }).join(', ');
  };

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <div 
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          border: '1px solid #ddd',
          borderRadius: '8px',
          padding: '10px 12px',
          background: 'white',
          cursor: 'pointer',
          minHeight: '42px'
        }}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span style={{ color: selectedCount > 0 ? '#333' : '#999' }}>
          {selectedCount > 0 
            ? `${selectedCount} worker${selectedCount !== 1 ? 's' : ''} selected` 
            : 'Search or select workers...'}
        </span>
        <span style={{ color: '#999' }}>{isOpen ? '▲' : '▼'}</span>
      </div>
      
      {isOpen && (
        <>
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 999
            }}
            onClick={() => setIsOpen(false)}
          />
          <div style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            background: 'white',
            border: '1px solid #ddd',
            borderRadius: '8px',
            marginTop: '4px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 1000,
            maxHeight: '380px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{ padding: '12px', borderBottom: '1px solid #eee' }}>
              <input
                type="text"
                placeholder="Search by name or role..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '13px',
                  outline: 'none'
                }}
                autoFocus
                onClick={(e) => e.stopPropagation()}
              />
            </div>
            <div style={{ maxHeight: '280px', overflowY: 'auto' }}>
              {filteredWorkers.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                  No workers found
                </div>
              ) : (
                filteredWorkers.map(worker => (
                  <label
                    key={worker.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '10px 12px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #f5f5f5',
                      transition: 'background 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                  >
                    <input
                      type="checkbox"
                      checked={selectedWorkerIds.includes(worker.id)}
                      onChange={() => onToggle(worker.id)}
                      style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '500', fontSize: '14px' }}>{worker.name}</div>
                      <div style={{ fontSize: '11px', color: '#666' }}>{worker.role?.replace('-', ' ')}</div>
                    </div>
                  </label>
                ))
              )}
            </div>
            {selectedCount > 0 && (
              <div style={{ 
                padding: '12px', 
                borderTop: '1px solid #eee',
                background: '#f9fafb',
                fontSize: '12px',
                color: '#333'
              }}>
                <strong>Selected ({selectedCount}):</strong> {getSelectedNames()}
              </div>
            )}
            <div style={{ padding: '12px', borderTop: '1px solid #eee' }}>
              <button 
                onClick={() => setIsOpen(false)}
                style={{
                  width: '100%',
                  padding: '8px',
                  background: '#800020',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Done
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const TaskAssignmentModal = ({ task, onClose, onAssign, currentUser, projectId }) => {
  const [timeFrameHours, setTimeFrameHours] = useState(24);
  const [assignmentNote, setAssignmentNote] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedWorkers, setSelectedWorkers] = useState([]);
  const [allWorkers, setAllWorkers] = useState([]);
  const [isMultipleMode, setIsMultipleMode] = useState(false);

  useEffect(() => {
    const fetchWorkers = async () => {
      const workersSnapshot = await getDocs(query(collection(db, 'users'), where('role', 'in', [
        'carpenter', 'electrician', 'machine-operator','workshop-manager','upholstery', 'assistant-carpenter','quality-control',
        'wood-polisher', 'designer', 'distributor','metalworks','accountant','manager', 'ceo',
        'upholstery','steel-works','marketing-sales'
      ])));
      const workersList = workersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAllWorkers(workersList);
      
      if (task.assignedTo) {
        if (Array.isArray(task.assignedTo)) {
          setSelectedWorkers(task.assignedTo);
        } else {
          setSelectedWorkers([task.assignedTo]);
        }
      }
    };
    fetchWorkers();
  }, [task]);

  const toggleWorker = (workerId) => {
    if (selectedWorkers.includes(workerId)) {
      setSelectedWorkers(selectedWorkers.filter(id => id !== workerId));
    } else {
      setSelectedWorkers([...selectedWorkers, workerId]);
    }
  };

  const getWorkerName = (workerId) => {
    const worker = allWorkers.find(w => w.id === workerId);
    return worker?.name || 'Unknown';
  };

  const handleAssign = async () => {
    if (selectedWorkers.length === 0) {
      alert('Please select at least one worker');
      return;
    }

    setIsLoading(true);
    const deadline = new Date();
    deadline.setHours(deadline.getHours() + timeFrameHours);
    
    try {
      const workerNames = [];
      for (const workerId of selectedWorkers) {
        const workerDoc = await getDoc(doc(db, 'users', workerId));
        if (workerDoc.exists()) {
          workerNames.push(workerDoc.data().name);
        }
      }
      
      await updateDoc(doc(db, 'tasks', task.id), {
        assignedTo: selectedWorkers,
        assignedToNames: workerNames,
        assignedBy: currentUser.uid,
        assignedAt: new Date(),
        timeFrameHours: timeFrameHours,
        deadline: deadline,
        assignmentNote: assignmentNote || `Task: ${task.title}`,
        status: 'pending',
        updatedAt: new Date()
      });
      
      for (const workerId of selectedWorkers) {
        await addDoc(collection(db, 'notifications'), {
          userId: workerId,
          type: 'task_assigned',
          title: 'New Task Assigned 📋',
          message: `${task.title}`,
          assignmentNote: assignmentNote || `Task: ${task.title}`,
          taskId: task.id,
          projectId: projectId,
          timeFrame: `${timeFrameHours}h`,
          read: false,
          createdAt: new Date()
        });
      }
      
      onAssign({ success: true });
      onClose();
    } catch (error) {
      console.error('Error assigning task:', error);
      alert('Failed to assign task');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '550px' }}>
        <h3>📋 Assign Task to Workers</h3>
        
        <div className="task-details-preview">
          <div className="preview-row"><span className="preview-label">Task:</span><span className="preview-value">{task.title}</span></div>
          <div className="preview-row"><span className="preview-label">Project:</span><span className="preview-value">{task.projectName || 'N/A'}</span></div>
        </div>
        
        <div className="assignment-mode-toggle">
          <button className={`mode-btn ${!isMultipleMode ? 'active' : ''}`} onClick={() => setIsMultipleMode(false)}>👤 Single Worker</button>
          <button className={`mode-btn ${isMultipleMode ? 'active' : ''}`} onClick={() => setIsMultipleMode(true)}>👥 Multiple Workers</button>
        </div>
        
        {!isMultipleMode ? (
          <div className="worker-selector">
            <label>Select Worker:</label>
            <SearchableWorkerDropdown 
              workers={allWorkers}
              selectedWorkerId={selectedWorkers[0] || ''}
              onSelect={(workerId) => setSelectedWorkers([workerId])}
              placeholder="Search or select worker..."
            />
          </div>
        ) : (
          <div className="multi-worker-selector">
            <label>Select Workers (can select multiple):</label>
            <SearchableMultiWorkerSelector 
              workers={allWorkers}
              selectedWorkerIds={selectedWorkers}
              onToggle={toggleWorker}
            />
          </div>
        )}
        
        <label>⏰ Time Frame:</label>
        <select 
          value={timeFrameHours} 
          onChange={(e) => setTimeFrameHours(Number(e.target.value))}
          style={{
            width: '100%',
            padding: '10px',
            borderRadius: '8px',
            border: '1px solid #ddd',
            marginBottom: '16px'
          }}
        >
          <option value={4}>4 hours (Urgent)</option>
          <option value={8}>8 hours (Same day)</option>
          <option value={24}>24 hours (1 day)</option>
          <option value={48}>48 hours (2 days)</option>
          <option value={72}>72 hours (3 days)</option>
          <option value={168}>1 week</option>
        </select>
        
        <label>📝 Assignment Note:</label>
        <textarea 
          value={assignmentNote} 
          onChange={(e) => setAssignmentNote(e.target.value)} 
          placeholder="Add context for workers..." 
          rows="3" 
          className="assignment-note-input"
          style={{
            width: '100%',
            padding: '10px',
            borderRadius: '8px',
            border: '1px solid #ddd',
            marginBottom: '8px',
            fontFamily: 'inherit'
          }}
        />
        <small className="note-hint">💡 Add context to help workers understand the task</small>
        
        <div className="modal-actions">
          <button 
            onClick={handleAssign} 
            className="assign-btn" 
            disabled={isLoading}
            style={{
              flex: 1,
              padding: '12px',
              background: '#800020',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontWeight: '600',
              opacity: isLoading ? 0.6 : 1
            }}
          >
            {isLoading ? 'Assigning...' : `✓ Assign to ${selectedWorkers.length} Worker${selectedWorkers.length !== 1 ? 's' : ''}`}
          </button>
          <button 
            onClick={onClose} 
            className="cancel-btn"
            style={{
              flex: 1,
              padding: '12px',
              background: '#f3f4f6',
              color: '#374151',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default TaskAssignmentModal;